import { useCallback, useState } from 'react';

import { ADMIN_PAGE_SIZES } from '@qaytim/shared';

/**
 * Sahifa raqamli sahifalash.
 *
 * Kursorli variantdan (`use-cursor-pager`, mijoz ilovasida qolgan) farqi bitta savolda: operator
 * ishni yarmida to'xtatib, keyin O'SHA sahifaga qaytadi. Kursorlar steki buni bera olmasdi —
 * u faqat "oldinga bosilgan yo'l" ni eslardi, ya'ni sahifani yangilash yoki havolani ulashish
 * hammasini boshiga qaytarardi.
 *
 * Chegara qiymatlar shu yerda ushlanadi: `total` javobdan keladi, ya'ni oxirgi sahifadagi
 * qatorlar o'chirilgach, joriy sahifa mavjud bo'lmay qolishi mumkin. Bunday holatda
 * `clampTo` ekranni bo'sh qoldirmasdan oxirgi mavjud sahifaga tortadi.
 */
export interface OffsetPager {
  page: number;
  limit: number;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  /** Filtr o'zgarganda: eski sahifa raqami yangi natijalar ichida ma'nosiz. */
  reset: () => void;
  /** Javobdagi `pageCount` ga qarab joriy sahifani mavjud oraliqqa tortadi. */
  clampTo: (pageCount: number) => void;
}

export interface OffsetPagerOptions {
  initialLimit?: number;
  /**
   * Boshqariladigan sahifa raqami — sahifa URL da yashaganda.
   *
   * Moderatsiya ekranida bu shart: operator qatordan detail sahifasiga o'tib, keyin qaytadi.
   * Sahifa faqat komponent holatida yashasa, qaytish uni har safar birinchi sahifaga tashlardi
   * — ya'ni detail sahifalarni qo'shish ro'yxatni yurishni buzardi.
   */
  page?: number;
  onPageChange?: (page: number) => void;
}

export const DEFAULT_PAGE_SIZE = ADMIN_PAGE_SIZES[1];

export function useOffsetPager(options: OffsetPagerOptions = {}): OffsetPager {
  const [internalPage, setInternalPage] = useState(1);
  const [limit, setLimitState] = useState(options.initialLimit ?? DEFAULT_PAGE_SIZE);

  const controlled = options.page !== undefined;
  const page = options.page ?? internalPage;
  const { onPageChange } = options;

  const setPage = useCallback(
    (next: number) => {
      const clamped = Math.max(1, next);
      // Boshqariladigan holatda ham ichki qiymat yangilanadi: `page` propi keyingi renderda
      // yetib kelmasa (masalan sinovda), pager o'z holatiga qaytib qolmasligi kerak.
      setInternalPage(clamped);
      onPageChange?.(clamped);
    },
    [onPageChange],
  );

  /*
    Sahifa hajmi o'zgarsa, raqam BOSHIGA qaytadi. "20 tadan 3-sahifa" va "100 tadan 3-sahifa"
    butunlay boshqa qatorlar: joyni saqlashga urinish (masalan `Math.ceil` bilan qayta hisoblash)
    operatorni o'zi ko'rmagan joyga tashlardi.
  */
  const setLimit = useCallback(
    (next: number) => {
      setLimitState(next);
      setPage(1);
    },
    [setPage],
  );

  const reset = useCallback(() => {
    setPage(1);
  }, [setPage]);

  const clampTo = useCallback(
    (pageCount: number) => {
      if (pageCount === 0) return;
      if (page > pageCount) setPage(pageCount);
    },
    [page, setPage],
  );

  return { page: controlled ? page : internalPage, limit, setPage, setLimit, reset, clampTo };
}
